/**
 * AdminItemMovementListPage — Issue #366.
 *
 * Append-only ledger of administrative item in / out / adjust movements.
 * Supports recording new movements and voiding existing rows (which inserts
 * a compensating opposite-sign row in the backend).
 *
 * 红线:
 *   - 数量字段以 decimal-safe string 流转；前端不允许 Number() 化。
 *   - 出库 / 入库 / 调整全部为追加；冲正显式调用后端 void 接口，原行不删除。
 */
import { useEffect, useMemo, useState } from 'react';
import { Link } from 'wouter';
import AccessDenied from '@/components/AccessDenied';
import { Card } from '@/components/ui/card';
import { useAuth } from '@/hooks/useAuth';
import {
  ADMIN_PROCUREMENT_PERMISSIONS,
  canManageAdminProcurement,
  hasPermission,
} from '@/lib/adminProcurementPermissions';
import {
  ADMIN_ITEM_MOVEMENT_TYPE_LABELS,
  ADMIN_PROCUREMENT_CATEGORY_LABELS,
  useAdminItemMovementMutations,
  useAdminItemMovements,
  type AdminItemMovementDto,
  type CreateAdminItemMovementDto,
} from '@/hooks/useAdminProcurement';

const EMPTY_FORM: CreateAdminItemMovementDto = {
  itemName: '',
  category: 'office_supplies',
  movementType: 'in',
  quantityDecimal: '1',
  unit: '件',
};

function fmtDateTime(value: string): string {
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleString('zh-CN');
}

const STATUS_BADGE_CLASS: Readonly<
  Record<AdminItemMovementDto['status'], string>
> = {
  active: 'bg-emerald-100 text-emerald-700',
  voided: 'bg-red-100 text-red-700',
};

const TYPE_BADGE_CLASS: Readonly<
  Record<AdminItemMovementDto['movementType'], string>
> = {
  in: 'bg-blue-100 text-blue-700',
  out: 'bg-amber-100 text-amber-700',
  adjust: 'bg-purple-100 text-purple-700',
};

export default function AdminItemMovementListPage() {
  const { permissions } = useAuth();
  const canRead = hasPermission(permissions, ADMIN_PROCUREMENT_PERMISSIONS.READ);
  const canWrite = hasPermission(
    permissions,
    ADMIN_PROCUREMENT_PERMISSIONS.WRITE,
  );
  const canManage = canManageAdminProcurement(permissions);

  const { items, total, loading, error, fetchMovements } = useAdminItemMovements();
  const {
    loading: mutating,
    error: mutationError,
    recordMovement,
    voidMovement,
  } = useAdminItemMovementMutations();

  const [itemName, setItemName] = useState('');
  const [typeFilter, setTypeFilter] = useState<
    AdminItemMovementDto['movementType'] | ''
  >('');
  const [statusFilter, setStatusFilter] = useState<
    AdminItemMovementDto['status'] | ''
  >('');
  const [page, setPage] = useState(1);
  const limit = 20;

  const [createOpen, setCreateOpen] = useState(false);
  const [form, setForm] = useState<CreateAdminItemMovementDto>(EMPTY_FORM);

  const reload = () => {
    void fetchMovements({
      itemName: itemName || undefined,
      movementType: typeFilter || undefined,
      status: statusFilter || undefined,
      page,
      limit,
    });
  };

  useEffect(() => {
    if (!canRead) return;
    void fetchMovements({
      itemName: itemName || undefined,
      movementType: typeFilter || undefined,
      status: statusFilter || undefined,
      page,
      limit,
    });
  }, [canRead, itemName, typeFilter, statusFilter, page, fetchMovements]);

  const totalPages = useMemo(
    () => Math.max(1, Math.ceil(total / limit)),
    [total],
  );

  if (!canRead) return <AccessDenied resource="行政物品出入库" />;

  const handleRecord = async () => {
    if (!form.itemName.trim() || !form.quantityDecimal.trim()) return;
    if (form.movementType === 'adjust' && !form.adjustDirection) {
      window.alert('盘点调整需要选择增加或减少方向');
      return;
    }
    const created = await recordMovement(form);
    if (created) {
      setCreateOpen(false);
      setForm(EMPTY_FORM);
      reload();
    }
  };

  const handleVoid = async (row: AdminItemMovementDto) => {
    const reason = window.prompt('请输入冲正原因');
    if (!reason || !reason.trim()) return;
    const next = await voidMovement(row.id, {
      expectedVersion: row.version,
      reason: reason.trim(),
    });
    if (next) reload();
  };

  return (
    <div className="space-y-4 p-6">
      <header className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-xl font-semibold">行政物品出入库</h1>
          <p className="text-sm text-slate-500">
            追加式记录办公耗材 / 家具 / 设备 的入库、出库、盘点调整。
            冲正通过后端 void 接口，自动追加反向流水，原行不会被物理删除。
          </p>
        </div>
        {canWrite ? (
          <button
            className="rounded-md border bg-blue-50 px-4 py-2 text-sm text-blue-700 hover:bg-blue-100"
            onClick={() => setCreateOpen(true)}
            data-testid="admin-movement-create-entry"
          >
            录入出入库 →
          </button>
        ) : null}
      </header>

      <div className="flex flex-wrap items-center gap-3">
        <label className="flex items-center gap-2 text-sm text-slate-600">
          物品
          <input
            type="text"
            value={itemName}
            placeholder="模糊匹配物品名称"
            className="rounded border border-slate-300 px-2 py-1 text-sm"
            onChange={(e) => {
              setItemName(e.target.value);
              setPage(1);
            }}
            data-testid="admin-movement-item-filter"
          />
        </label>
        <label className="flex items-center gap-2 text-sm text-slate-600">
          类型
          <select
            className="rounded border border-slate-300 px-2 py-1 text-sm"
            value={typeFilter}
            onChange={(e) => {
              setTypeFilter(
                (e.target.value as AdminItemMovementDto['movementType']) || '',
              );
              setPage(1);
            }}
            data-testid="admin-movement-type-filter"
          >
            <option value="">全部</option>
            {(
              Object.entries(ADMIN_ITEM_MOVEMENT_TYPE_LABELS) as [
                AdminItemMovementDto['movementType'],
                string,
              ][]
            ).map(([value, label]) => (
              <option key={value} value={value}>
                {label}
              </option>
            ))}
          </select>
        </label>
        <label className="flex items-center gap-2 text-sm text-slate-600">
          状态
          <select
            className="rounded border border-slate-300 px-2 py-1 text-sm"
            value={statusFilter}
            onChange={(e) => {
              setStatusFilter(
                (e.target.value as AdminItemMovementDto['status']) || '',
              );
              setPage(1);
            }}
            data-testid="admin-movement-status-filter"
          >
            <option value="">全部</option>
            <option value="active">有效</option>
            <option value="voided">已冲正</option>
          </select>
        </label>
        <Link
          href="/admin/procurement"
          className="ml-auto text-sm text-blue-600 hover:underline"
        >
          返回采购台账 →
        </Link>
      </div>

      {error ? (
        <Card
          className="border-red-200 bg-red-50 p-4 text-sm text-red-800"
          data-testid="admin-movement-error"
        >
          {error.message}
        </Card>
      ) : null}
      {mutationError ? (
        <Card
          className="border-red-200 bg-red-50 p-4 text-sm text-red-800"
          data-testid="admin-movement-mutation-error"
        >
          {mutationError.message}
        </Card>
      ) : null}

      <Card className="overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-left text-xs uppercase text-slate-500">
            <tr>
              <th className="px-3 py-2">发生时间</th>
              <th className="px-3 py-2">物品</th>
              <th className="px-3 py-2">类别</th>
              <th className="px-3 py-2">类型</th>
              <th className="px-3 py-2">数量</th>
              <th className="px-3 py-2">关联采购</th>
              <th className="px-3 py-2">状态</th>
              <th className="px-3 py-2">操作</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td
                  className="px-3 py-6 text-center text-slate-500"
                  colSpan={8}
                  data-testid="admin-movement-loading"
                >
                  加载中…
                </td>
              </tr>
            ) : items.length === 0 ? (
              <tr>
                <td
                  className="px-3 py-6 text-center text-slate-500"
                  colSpan={8}
                  data-testid="admin-movement-empty"
                >
                  暂无流水记录
                </td>
              </tr>
            ) : (
              items.map((it) => (
                <tr
                  key={it.id}
                  className="border-t border-slate-100"
                  data-testid={`admin-movement-row-${it.id}`}
                >
                  <td className="px-3 py-2 text-xs">{fmtDateTime(it.occurredAt)}</td>
                  <td className="px-3 py-2">{it.itemName}</td>
                  <td className="px-3 py-2">
                    {ADMIN_PROCUREMENT_CATEGORY_LABELS[it.category]}
                  </td>
                  <td className="px-3 py-2">
                    <span
                      className={`inline-block rounded px-2 py-0.5 text-xs ${TYPE_BADGE_CLASS[it.movementType]}`}
                    >
                      {ADMIN_ITEM_MOVEMENT_TYPE_LABELS[it.movementType]}
                    </span>
                  </td>
                  <td className="px-3 py-2 font-mono text-xs">
                    {it.quantityDecimal} {it.unit}
                  </td>
                  <td className="px-3 py-2 font-mono text-xs">
                    {it.relatedPurchaseId ? it.relatedPurchaseId.slice(0, 12) : '—'}
                  </td>
                  <td className="px-3 py-2">
                    <span
                      className={`inline-block rounded px-2 py-0.5 text-xs ${STATUS_BADGE_CLASS[it.status]}`}
                    >
                      {it.status === 'active' ? '有效' : '已冲正'}
                    </span>
                  </td>
                  <td className="px-3 py-2 text-xs">
                    {canManage && it.status === 'active' ? (
                      <button
                        className="text-red-600 hover:underline"
                        disabled={mutating}
                        onClick={() => handleVoid(it)}
                        data-testid={`admin-movement-void-${it.id}`}
                      >
                        冲正
                      </button>
                    ) : null}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </Card>

      <div className="flex items-center justify-between text-sm text-slate-600">
        <span>
          共 {total} 条 / 第 {page} / {totalPages} 页
        </span>
        <div className="flex gap-2">
          <button
            className="rounded border px-3 py-1 text-sm disabled:opacity-50"
            disabled={page <= 1 || loading}
            onClick={() => setPage((p) => Math.max(1, p - 1))}
          >
            上一页
          </button>
          <button
            className="rounded border px-3 py-1 text-sm disabled:opacity-50"
            disabled={page >= totalPages || loading}
            onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
          >
            下一页
          </button>
        </div>
      </div>

      {createOpen ? (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/40"
          data-testid="admin-movement-create-dialog"
        >
          <Card className="w-full max-w-lg space-y-3 p-6">
            <h2 className="text-base font-semibold">录入出入库 / 调整</h2>
            <label className="block text-sm">
              <span className="block text-slate-600">物品名称</span>
              <input
                type="text"
                className="mt-1 w-full rounded border border-slate-300 px-2 py-1"
                value={form.itemName}
                onChange={(e) =>
                  setForm((f) => ({ ...f, itemName: e.target.value }))
                }
                data-testid="admin-movement-create-name"
              />
            </label>
            <label className="block text-sm">
              <span className="block text-slate-600">类别</span>
              <select
                className="mt-1 w-full rounded border border-slate-300 px-2 py-1"
                value={form.category}
                onChange={(e) =>
                  setForm((f) => ({
                    ...f,
                    category: e.target.value as AdminItemMovementDto['category'],
                  }))
                }
              >
                {(
                  Object.entries(ADMIN_PROCUREMENT_CATEGORY_LABELS) as [
                    AdminItemMovementDto['category'],
                    string,
                  ][]
                ).map(([value, label]) => (
                  <option key={value} value={value}>
                    {label}
                  </option>
                ))}
              </select>
            </label>
            <label className="block text-sm">
              <span className="block text-slate-600">类型</span>
              <select
                className="mt-1 w-full rounded border border-slate-300 px-2 py-1"
                value={form.movementType}
                onChange={(e) =>
                  setForm((f) => ({
                    ...f,
                    movementType:
                      e.target.value as AdminItemMovementDto['movementType'],
                  }))
                }
                data-testid="admin-movement-create-type"
              >
                {(
                  Object.entries(ADMIN_ITEM_MOVEMENT_TYPE_LABELS) as [
                    AdminItemMovementDto['movementType'],
                    string,
                  ][]
                ).map(([value, label]) => (
                  <option key={value} value={value}>
                    {label}
                  </option>
                ))}
              </select>
            </label>
            {form.movementType === 'adjust' ? (
              <label className="block text-sm">
                <span className="block text-slate-600">调整方向</span>
                <select
                  className="mt-1 w-full rounded border border-slate-300 px-2 py-1"
                  value={form.adjustDirection ?? ''}
                  onChange={(e) =>
                    setForm((f) => ({
                      ...f,
                      adjustDirection:
                        (e.target.value as 'increase' | 'decrease') || undefined,
                    }))
                  }
                  data-testid="admin-movement-create-direction"
                >
                  <option value="">请选择</option>
                  <option value="increase">增加</option>
                  <option value="decrease">减少</option>
                </select>
              </label>
            ) : null}
            <label className="block text-sm">
              <span className="block text-slate-600">数量 (decimal-safe)</span>
              <input
                type="text"
                inputMode="decimal"
                className="mt-1 w-full rounded border border-slate-300 px-2 py-1"
                value={form.quantityDecimal}
                onChange={(e) =>
                  setForm((f) => ({ ...f, quantityDecimal: e.target.value }))
                }
                data-testid="admin-movement-create-qty"
              />
            </label>
            <label className="block text-sm">
              <span className="block text-slate-600">单位</span>
              <input
                type="text"
                className="mt-1 w-full rounded border border-slate-300 px-2 py-1"
                value={form.unit ?? ''}
                onChange={(e) => setForm((f) => ({ ...f, unit: e.target.value }))}
              />
            </label>
            <label className="block text-sm">
              <span className="block text-slate-600">关联采购单 ID (可选)</span>
              <input
                type="text"
                className="mt-1 w-full rounded border border-slate-300 px-2 py-1 font-mono text-xs"
                value={form.relatedPurchaseId ?? ''}
                onChange={(e) =>
                  setForm((f) => ({ ...f, relatedPurchaseId: e.target.value }))
                }
              />
            </label>
            <label className="block text-sm">
              <span className="block text-slate-600">备注 (可选)</span>
              <textarea
                className="mt-1 w-full rounded border border-slate-300 px-2 py-1"
                value={form.reason ?? ''}
                onChange={(e) => setForm((f) => ({ ...f, reason: e.target.value }))}
              />
            </label>
            <div className="flex justify-end gap-2 pt-2">
              <button
                className="rounded border px-3 py-1 text-sm"
                onClick={() => setCreateOpen(false)}
              >
                取消
              </button>
              <button
                className="rounded bg-blue-600 px-3 py-1 text-sm text-white disabled:opacity-50"
                disabled={mutating}
                onClick={handleRecord}
                data-testid="admin-movement-create-submit"
              >
                提交
              </button>
            </div>
          </Card>
        </div>
      ) : null}
    </div>
  );
}
